import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ScreenSize } from 'libs/portal/src/layout/layout.service';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { CardTableOptions } from 'libs/portal/src/modules/tools/card-table/models';
import { CardTableHeader } from 'libs/portal/src/modules/tools/card-table/models/card-table-header.model';
import { Endpoints } from '@huge-monorepo/portal/config';
import { GenericTableHeaderOptions, RefreshData } from '../../tools/card-table/models/generic-table-header-options.model';
import { openExportDialog } from '../../tools/export-modal/export-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-sim-usage-report',
  templateUrl: './sim-usage-report.component.html',
  styleUrls: ['./sim-usage-report.component.scss'],
})
export class SimUsageReportComponent implements OnInit, OnDestroy {
  search = '';
  count = 10;
  offset = 0;
  orderBy = '';
  desc = false;
  currentPage = 1;
  totalItems: number;

  tableSelectionValue: 'device' | 'sim' = 'sim';

  items: any[] = [];
  allItemsSelected = false;
  selectedItems: any[] = [];
  loading = false;
  downloadingReport = false;

  obfuscateData = false;
  authSubscription: Subscription;

  ScreenSize = ScreenSize;
  searched = false;

  cdrTableOptions: CardTableOptions = {
    titleProperty: 'name',
    subTitleProperty: '',
    additionalProperty: 'data',
    rowId: 'id',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: true,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'selected', 'id', 'bundle'],
    stylePropertyPrefix: 'devices',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  simTableOptions: CardTableOptions = {
    titleProperty: 'name',
    subTitleProperty: '',
    additionalProperty: 'data',
    rowId: 'id',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: true,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'selected', 'id'],
    stylePropertyPrefix: 'devices',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  cdrHeaderOptions: GenericTableHeaderOptions = {
    ShowSearch: true,
    SearchPlaceholder: 'Search devices',
    DefaultDesc: true,
    EnableExport: true,
    EnableSelectAll: true,
    DateItem: {
      Singular: false,
      StartDate: this.tools.StartOfMonth().toDateString(),
      EndDate: new Date().toDateString(),
    },
    DateRequired: true,
    SortItems: [
      {
        Name: 'DeviceType',
        Display: 'Type',
      },
      {
        Name: 'CustomerTradingName',
        Display: 'Customer',
      },
      {
        Name: 'TerminalID',
        Display: 'Terminal ID',
      },
      {
        Name: 'MerchantName',
        Display: 'Merchant',
      },
    ],
  };

  cdrTableHeaders: CardTableHeader[] = [
    {
      ID: 'name',
      Text: 'Name',
      Width: 15,
    },
    {
      ID: 'deviceType',
      Text: 'Type',
      Width: 10,
    },
    {
      ID: 'customer',
      Text: 'Customer',
      Width: 30,
    },
    {
      ID: 'TerminalID',
      Text: 'Terminal ID',
      Width: 15,
    },
    {
      ID: 'MerchantName',
      Text: 'Merchant',
      Width: 15,
    },
    {
      ID: 'SerialNumber',
      Text: 'Serial Number',
      Width: 10,
    },
  ];

  simHeaderOptions: GenericTableHeaderOptions = {
    ShowSearch: true,
    SearchPlaceholder: 'Search sims',
    DefaultDesc: true,
    EnableExport: true,
    EnableSelectAll: true,
    DateRequired: true,
    DateItem: {
      Singular: false,
      StartDate: this.tools.StartOfMonth().toDateString(),
      EndDate: new Date().toDateString(),
    },
    SortItems: [
      {
        Name: 'SimNum',
        Display: 'Name',
      },
      {
        Name: 'SimCell',
        Display: 'Cell',
      },
      {
        Name: 'Status',
        Display: 'Status',
      },
    ],
  };

  simTableHeaders: CardTableHeader[] = [
    {
      ID: 'name',
      Text: 'Name',
      Width: 15,
    },
    {
      ID: 'simCell',
      Text: 'Cell',
      Width: 15,
    },
    {
      ID: 'status',
      Text: 'Status',
      Width: 15,
    },
    {
      ID: 'ServiceProvider',
      Text: 'Service Provider',
      Width: 30,
    },
    {
      ID: 'DeviceNum',
      Text: 'Device Num',
      Width: 15,
    },
  ];

  constructor(
    private endpoints: Endpoints,
    private auth: UserRightsService,
    private genericService: GenericService,
    private tools: ToolsService,
    private dialogRef: MatDialog,
    private toastService: ToastrService,
  ) {}

  ngOnInit() {
    this.authSubscription = this.auth.GetUser().subscribe((user) => {
      this.obfuscateData = user.isObfuscated;
      if (this.tableSelectionValue == 'device') {
        this.getDevices(this.orderBy, this.search, this.count, this.offset, this.desc);
      } else {
        this.getSims(this.orderBy, this.search, this.count, this.offset, this.desc);
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  getDevices(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.loading = true;
    const obj = {
      Online: null,
      BundleStart: null,
      BundleEnd: null,
      GroupID: this.auth.GetGroupID(),
    };

    this.genericService.POST(this.endpoints.GET_CDR_DEVICES(search, count, offset, orderBy, desc), obj).subscribe((resp) => {
      const deviceData = resp['body'];
      this.items = [];
      if (deviceData != null) {
        const tmpData = [];
        deviceData.forEach((element) => {
          const tempObj = {
            id: element.id,
            name: element.name,
            deviceType: element.deviceType,
            customer: element.CustomerTradingName ? (this.obfuscateData ? this.tools.Obfuscate(element.CustomerTradingName, 'Customer', 8) : element.CustomerTradingName) : '',
            selected: false,
            bundle: element.InBundleAmount,
            color: '#949494',
            terminalID: element.TerminalID ?? '',
            merchantName: element.MerchantName ?? '',
            serialNumber: element.SerialNumber ?? '',
          };

          tmpData.push(tempObj);
        });

        this.items = tmpData;
      } else {
        this.items = [];
      }

      this.totalItems = this.tools.ExtractPaginationFromHeaders(resp['headers']);

      this.loading = false;
    });
  }

  getSims(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.loading = true;
    const obj = {
      Online: null,
      GroupID: this.auth.GetGroupID(),
    };

    this.genericService.POST(this.endpoints.GET_SIMS(search, count, offset, orderBy, desc), obj).subscribe((resp) => {
      const simData = resp['body'];
      this.items = [];
      if (simData != null) {
        const tmpData = [];
        simData.forEach((element) => {
          const tempObj = {
            id: element.SimID,
            name: element.SimNum,
            simCell: element.SimCell,
            status: element.Status,
            selected: false,
            color: '#949494',
            serviceprovider: element.ServiceProvider ?? '',
            devicenum: element.DeviceNum ?? '',
          };

          tmpData.push(tempObj);
        });
        this.items = tmpData;
      } else {
        this.items = [];
      }

      this.totalItems = this.tools.ExtractPaginationFromHeaders(resp['headers']);

      this.loading = false;
    });
  }

  paginateDevices(pageIndex: number) {
    this.offset = (pageIndex - 1) * this.count;
    this.currentPage = pageIndex;
    this.getDevices(this.orderBy, this.search, this.count, this.offset, this.desc);
  }

  paginateSims(pageIndex: number) {
    this.offset = (pageIndex - 1) * this.count;
    this.currentPage = pageIndex;
    this.getSims(this.orderBy, this.search, this.count, this.offset, this.desc);
  }

  selectAllDevices(selected: boolean) {
    this.allItemsSelected = selected;
    if (this.items == null) {
      return;
    }
    this.items.forEach((t) => {
      t.selected = selected;
      if (selected) {
        this.selectedItems.push(t.id);
      } else {
        this.selectedItems = [];
      }
    });
  }

  selectDevice(ev) {
    const device = this.items.find((t) => t.id == ev.row.id);
    console.log(ev);
    console.log(device);
    if (device != null) {
      device.selected = ev.checked;
      if (ev.checked) {
        this.selectedItems.push(ev.row.id);
      } else {
        const removeIndex = this.selectedItems.findIndex((t) => t == ev.row.id);
        this.selectedItems.splice(removeIndex, 1);
      }
    }
    console.log(this.selectedItems);
  }

  toggleDevices() {
    this.loading = true;
    this.items = [];
    this.allItemsSelected = false;
    this.selectedItems = [];
    this.search = '';
    if (this.tableSelectionValue == 'device') {
      this.getSims(this.orderBy, this.search, this.count, this.offset, this.desc);
      this.tableSelectionValue = 'sim';
    } else {
      this.getDevices(this.orderBy, this.search, this.count, this.offset, this.desc);
      this.tableSelectionValue = 'device';
    }
  }

  refreshTable(data: RefreshData): void {
    this.search = data.Search ?? '';
    if (this.search) {
      this.searched = true;
    } else {
      this.searched = false;
    }
    this.orderBy = data.Sort ?? '';
    this.desc = data.Desc ?? this.desc;
    this.offset = 0;
    this.currentPage = 1;
    if (this.tableSelectionValue == 'device') {
      this.getDevices(this.orderBy, this.search, this.count, this.offset, this.desc);
    } else {
      this.getSims(this.orderBy, this.search, this.count, this.offset, this.desc);
    }
  }

  onRowSelected(data: any) {
    if (data.selected) {
      // Add the ID if it isn't already in the array
      if (!this.selectedItems.includes(data.id)) {
        this.selectedItems.push(data.id);
      }
    } else {
      // Remove the ID if it exists in the array
      this.selectedItems = this.selectedItems.filter(id => id !== data.id);
    }
    this.allItemsSelected = false;
  }

  exportReport(data: RefreshData): void {
    this.downloadingReport = true;
    if (this.allItemsSelected || this.selectedItems.length > 0) {
      
      let obj = {
        StartDate: data.FromDate,
        EndDate: data.ToDate,
        AllDevices: false,
        AllSims: false,
        AllDeviceTypes: true,
        DeviceIds: this.tableSelectionValue == 'device' ? this.selectedItems : [],
        DeviceTypes: [],
        CustomerIds: [],
        SimIds: this.tableSelectionValue == 'sim' ? this.selectedItems : [],
        AggregateIds: [],
        DeviceSearchTerm: this.tableSelectionValue == 'device' ? this.search : '',
        CustomerSearchTerm: '',
        AggregateSearchTerm: '',
        SimSearchTerm: this.tableSelectionValue == 'sim' ? this.search : '',
      };
      
      if(this.searched == false){
        obj = {
          StartDate: data.FromDate,
          EndDate: data.ToDate,
          AllDevices: this.tableSelectionValue == 'device' ? this.allItemsSelected : false,
          AllSims: this.tableSelectionValue == 'sim' ? this.allItemsSelected : false,
          AllDeviceTypes: true,
          DeviceIds: !this.allItemsSelected && this.tableSelectionValue == 'device' ? this.selectedItems : [],
          DeviceTypes: [],
          CustomerIds: [],
          SimIds: !this.allItemsSelected && this.tableSelectionValue == 'sim' ? this.selectedItems : [],
          AggregateIds: [],
          DeviceSearchTerm: this.tableSelectionValue == 'device' ? this.search : '',
          CustomerSearchTerm: '',
          AggregateSearchTerm: '',
          SimSearchTerm: this.tableSelectionValue == 'sim' ? this.search : '',
        };
      }

      openExportDialog(this.dialogRef, {
        endpoint: this.endpoints.DOWNLOAD_SIM_USAGE_REPORT,
        reqObj: obj,
      });
    } else {
      this.toastService.info('', 'No devices selected', { timeOut: 1000 });
    }
  }
}
